import { Navigate, Outlet, useParams, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useMultiRoleAccess } from "@/hooks/useMultiRoleAccess";
import { useEffect, useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

// Minimalist loading component matching N71 aesthetic
function MinimalLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <span className="text-sm font-medium text-foreground/30 tracking-wider animate-pulse">
        N71
      </span>
    </div>
  );
}

export function RequireAuth() {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <MinimalLoader />;
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return <Outlet />;
}

export function RequireWorkspace() {
  const { workspaceId } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { isLoading, workspaces, currentWorkspace, refreshWorkspaces } = useWorkspace();
  const [membershipCheck, setMembershipCheck] = useState<{ isActive: boolean; role?: string } | null>(null);
  const [checkLoading, setCheckLoading] = useState(true);
  const [oauthWaitComplete, setOauthWaitComplete] = useState(false);
  const oauthWaitStarted = useRef(false);

  // Detect OAuth callback - user returning from integration connection
  const isOAuthCallback = searchParams.get('connected') || searchParams.get('error');

  // Wait for OAuth callback to settle before checking workspaces
  // This prevents premature redirect to /no-workspace while workspace data is loading
  useEffect(() => {
    if (isOAuthCallback && !oauthWaitStarted.current) {
      oauthWaitStarted.current = true;
      console.log('[RequireWorkspace] OAuth callback detected, waiting for workspace data...');
      
      const timer = setTimeout(async () => {
        console.log('[RequireWorkspace] Refreshing workspaces after OAuth...');
        await refreshWorkspaces();
        setOauthWaitComplete(true);
      }, 2000); // Give backend time to sync workspace associations
      
      return () => clearTimeout(timer);
    } else if (!isOAuthCallback) {
      // Not an OAuth callback, no need to wait
      setOauthWaitComplete(true);
    }
  }, [isOAuthCallback, refreshWorkspaces]);

  useEffect(() => {
    const checkMembership = async () => {
      if (!user || !workspaceId) {
        setCheckLoading(false);
        return;
      }

      try {
        // Check if user is an active member of this workspace
        const { data: identities, error: idError } = await supabase
          .from('person_identities')
          .select('person_id')
          .eq('provider_user_id', user.id)
          .in('provider', ['supabase_auth', 'auth'])
          .eq('active', true)
          .eq('workspace_id', workspaceId)
          .limit(2);

        if (idError) {
          console.warn('[RequireWorkspace] identity lookup error (non-fatal):', idError.message);
        }

        const personId = identities && identities.length > 0 ? identities[0].person_id : undefined;

        if (personId) {
          const { data: membership } = await supabase
            .from('workspace_members')
            .select('is_active, workspace_role')
            .eq('workspace_id', workspaceId)
            .eq('person_id', personId)
            .maybeSingle();

          // Normalize snake_case from DB to camelCase for FE logic
          const normalized = membership
            ? { isActive: (membership as any).is_active === true, role: (membership as any).workspace_role as string | undefined }
            : null;

          if (import.meta.env.DEV) {
            console.debug('[RequireWorkspace] membership check', {
              userId: user.id,
              workspaceId,
              normalized,
            });
          }

          setMembershipCheck(normalized);
        }
      } catch (e) {
        console.error('Membership check error:', e);
      } finally {
        setCheckLoading(false);
      }
    };

    checkMembership();
  }, [user, workspaceId]);

  if (isLoading || checkLoading) {
    return <MinimalLoader />;
  }

  // Wait for OAuth callback to settle before any redirects
  if (isOAuthCallback && !oauthWaitComplete) {
    return <MinimalLoader />;
  }

  // Redirect inactive members to awaiting-role (except if already there or on setup).
  // Admins are never blocked by awaiting-role.
  if (
    workspaceId &&
    membershipCheck &&
    membershipCheck.role !== 'admin' &&
    membershipCheck.isActive === false
  ) {
    if (!location.pathname.includes('/awaiting-role') && !location.pathname.includes('/setup')) {
      return <Navigate to="/awaiting-role" replace />;
    }
  }

  // If a workspaceId route param is present, ensure user has access to it
  if (workspaceId) {
    const hasAccess =
      !!currentWorkspace && currentWorkspace.id === workspaceId ||
      workspaces.some(w => w.id === workspaceId);

    if (!hasAccess) {
      return <Navigate to="/no-workspace" replace />;
    }
  } else {
    // Routes that require any workspace
    if (!currentWorkspace && workspaces.length === 0) {
      return <Navigate to="/no-workspace" replace />;
    }
  }

  return <Outlet />;
}

interface RequireRoleProps {
  allowedRoles: Array<'ic' | 'manager' | 'executive' | 'admin'>;
}

export function RequireRole({ allowedRoles }: RequireRoleProps) {
  const { roles, isLoading } = useMultiRoleAccess();
  const location = useLocation();

  if (isLoading) {
    return <MinimalLoader />;
  }

  const hasAccess = roles.some(role => (allowedRoles as any).includes(role));

  if (!hasAccess) {
    // If roles are not resolved yet or empty, allow employee routes as safe default
    if (roles.length === 0 && allowedRoles.includes('ic')) {
      return <Outlet />;
    }
    // Otherwise send users to Context dashboard
    return <Navigate to="/context/dashboard" state={{ from: location }} replace />;
  }

  return <Outlet />;
}
